import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import './Expenses.css';
import { useAuth } from '../../context/AuthContext';

const EMPTY = {
  expense_date: today(),
  mode: 'Cash',
  reference_no: '',
  file: null,
  items: [{ category_id: '', payee: '', amount: '', description: '' }],
};

export default function Expenses({ entryDate }) {
  const { t } = useTranslation();
  const { can, canAccess } = useAuth();
  const baseDate = entryDate || today();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState(entryDate || today());
  const [catFilter, setCatFilter] = useState('');

  React.useEffect(() => {
    // Inside Daily Entry, scope the list to the single business date; standalone
    // defaults to the current month-to-date range.
    if (entryDate) { setFrom(entryDate); setTo(entryDate); return; }
    const d = new Date();
    setFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10));
  }, [entryDate]);

  const { data, loading, error, reload } = useApi(
    () => from && to ? api.expenses.list(from, to, catFilter) : Promise.resolve({ rows: [], total: 0, by_category: {} }),
    [from, to, catFilter],
    { rows: [], total: 0, by_category: {}, count: 0 }
  );

  const { data: categories, loading: catsLoading, error: catError, reload: reloadCats } = useApi(() => api.expenses.categories(), [], []);
  const { data: banksData } = useApi(() => api.banks.list(), [], { banks: [] });

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, expense_date: baseDate });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const [newCat, setNewCat] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const addItem = () => setForm({ ...form, items: [...form.items, { category_id: '', payee: '', amount: '', description: '' }] });
  const removeItem = (idx) => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  const updateItem = (idx, f, v) => {
    const it = [...form.items];
    it[idx][f] = v;
    setForm({ ...form, items: it });
  };
  const handleViewAttachment = async (id) => {
    try {
      const blob = await api.expenses.downloadAttachment(id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      notify(`❌ ${t('common.error')}: ` + e.message);
    }
  };

  const handleSave = async () => {
    const validItems = form.items.filter(it => it.category_id && Number(it.amount) > 0);
    if (validItems.length === 0) { notify('❌ ' + t('expenses.errors.noValidItem')); return; }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('expense_date', form.expense_date);
      fd.append('mode', form.mode);
      fd.append('reference_no', form.reference_no);
      if (form.file) fd.append('file', form.file);
      fd.append('items', JSON.stringify(validItems));

      if (editId) {
        // Edit is still single row in this version for simplicity, but backend handles multi-save for new
        await api.expenses.update(editId, { ...validItems[0], expense_date: form.expense_date, mode: form.mode, reference_no: form.reference_no });
        notify('✅ ' + t('expenses.toasts.expenseUpdated'));
      } else {
        await api.expenses.create(fd);
        notify('✅ ' + t('expenses.toasts.expensesRecorded'));
      }

      setShowForm(false);
      setForm({ ...EMPTY, expense_date: baseDate });
      setEditId(null);
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setSaving(false);
  };

  const handleAddCategory = async () => {
    const n = newCat.trim();
    if (!n) return;
    try {
      await api.expenses.addCategory(n);
      notify('✅ ' + t('expenses.toasts.categoryAdded'));
      setNewCat('');
      reloadCats();
    } catch (e) {
      notify('❌ ' + e.message);
    }
  };

  const handleDelete = async () => {
    try {
      await api.expenses.remove(confirm.id);
      notify('✅ ' + t('expenses.toasts.expenseDeleted'));
      reload();
    } catch (e) {
      notify('❌ ' + e.message);
    }
    setConfirm(null);
  };

  const openEdit = (r) => {
    setEditId(r.id);
    setForm({
      expense_date: r.expense_date,
      mode: r.mode,
      reference_no: r.reference_no,
      file: null,
      items: [{
        category_id: r.category_id,
        payee: r.payee,
        amount: r.amount,
        description: r.description
      }]
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const banks = banksData.banks || [];
  if (!canAccess('expenses')) {
    return (
      <div className="expenses-page">
        <ErrBanner msg={t('expenses.accessDenied')} />
      </div>
    );
  }
  return (
    <div className="expenses-page">
      <div className="page-header">
        <h2 className="page-title">{t('expenses.title')}</h2>
        {can('expenses', 'create') && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              if (showForm) { setShowForm(false); setEditId(null); setForm({ ...EMPTY, expense_date: baseDate }); }
              else { setShowForm(true); setEditId(null); setForm({ ...EMPTY, expense_date: baseDate }); }
            }}
          >
            {showForm ? `✕ ${t('common.cancel')}` : `＋ ${t('expenses.newExpense')}`}
          </button>
        )}
      </div>

      {msg && <div className="exp-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}
      {catError && <ErrBanner msg={t('expenses.categoriesError', { error: catError })} />}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{editId ? `✏️ ${t('expenses.editExpenses', { date: fmtDate(form.expense_date) })}` : t('expenses.recordNewExpense')}</div>
          <div style={{ padding: '0 16px 16px' }}>
            <div className="exp-form-grid" style={{ marginBottom: 16 }}>
              <div className="form-group">
                <label>{t('common.date')} *</label>
                <input type="date" value={form.expense_date}
                  onChange={e => setForm({ ...form, expense_date: e.target.value })} />
              </div>
              <div className="form-group">
                <label>{t('expenses.mode')} *</label>
                <select value={form.mode} onChange={e => setForm({ ...form, mode: e.target.value })}>
                  <option value="Cash">{t('common.cash')}</option>
                  {banks.map(b => <option key={b.id} value={`Bank:${b.id}`}>{b.name}</option>)}
                  <option value="UPI">UPI</option>
                </select>
              </div>
              <div className="form-group">
                <label>{t('expenses.refBillNo')}</label>
                <input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} />
              </div>
              <div className="form-group">
                <label>{t('expenses.attachment')}</label>
                <div className="custom-file-upload">
                  {form.file ? `📄 ${form.file.name}` : `📁 ${t('expenses.chooseFile')}`}
                  <input type="file" onChange={e => setForm({ ...form, file: e.target.files[0] })} />
                </div>
              </div>
            </div>

            <table className="data-table" style={{ marginBottom: 16 }}>
              <thead>
                <tr>
                  <th style={{ width: '25%' }}>{t('expenses.category')}</th>
                  <th style={{ width: '20%' }}>{t('expenses.payee')}</th>
                  <th style={{ width: '15%' }}>{t('common.amount')}</th>
                  <th>{t('common.description')}</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>
                      <select className="form-control" value={it.category_id} onChange={e => updateItem(idx, 'category_id', e.target.value)}>
                        <option value="">{`-- ${t('common.select')} --`}</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </td>
                    <td><input className="form-control" value={it.payee} onChange={e => updateItem(idx, 'payee', e.target.value)} /></td>
                    <td><input type="number" className="form-control" value={it.amount} onChange={e => updateItem(idx, 'amount', e.target.value)} /></td>
                    <td><input className="form-control" value={it.description} onChange={e => updateItem(idx, 'description', e.target.value)} /></td>
                    <td>
                      {form.items.length > 1 && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }} onClick={() => removeItem(idx)}>🗑️</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button className="btn btn-secondary btn-sm" onClick={addItem}>＋ {t('expenses.addAnotherLine')}</button>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder={t('expenses.newCategory')} style={{ width: 120 }} className="form-control" />
                  <button className="btn btn-sm" onClick={handleAddCategory}>+ {t('common.add')}</button>
                </div>
                {((editId && can('expenses', 'update')) || (!editId && can('expenses', 'create'))) && (
                  <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
                    {saving ? `⏳ ${t('common.saving')}` : editId ? t('common.update') : t('expenses.saveExpense')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="exp-filters">
        <div className="form-group">
          <label>{t('common.from')}</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{t('common.to')}</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <div className="form-group">
          <label>{t('expenses.category')}</label>
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)}>
            <option value="">{t('common.all')}</option>
            {(categories || []).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
          <strong>{t('common.total')}: </strong>
          <span style={{ fontWeight: 700, fontFamily: 'monospace', color: 'var(--danger)' }}>
            ₹ {fmtNum(data.total)}
          </span>
          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{' · '}{t('expenses.entriesCount', { n: data.count })}</span>
        </div>
      </div>

      {/* Category breakdown */}
      {Object.keys(data.by_category || {}).length > 0 && (
        <div className="section-card">
          <div className="section-header">{t('expenses.byCategory')}</div>
          <div className="exp-category-grid">
            {Object.entries(data.by_category).map(([name, total]) => (
              <div key={name} className="exp-cat-tile">
                <div className="exp-cat-name">{name}</div>
                <div className="exp-cat-amount">
                  ₹ {fmtNum(total)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{t('expenses.expenseEntries')}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.voucher')}</th>
                <th style={{ textAlign: 'left' }}>{t('expenses.category')}</th>
                <th style={{ textAlign: 'left' }}>{t('expenses.payee')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.description')}</th>
                <th style={{ textAlign: 'left' }}>{t('expenses.mode')}</th>
                <th style={{ textAlign: 'right' }}>{t('common.amount')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.rows.length === 0 ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
                  {t('expenses.noExpenses')}
                </td></tr>
              ) : data.rows.map(r => (
                <tr key={r.id}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(r.expense_date)}</td>
                  <td style={{ textAlign: 'left', fontWeight: 600, fontFamily: 'monospace' }}>
                    {r.voucher_no}
                    {r.file_path && (
                      <button onClick={() => handleViewAttachment(r.id)} title={t('expenses.viewAttachment')} style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer' }}>📎</button>
                    )}
                  </td>
                  <td style={{ textAlign: 'left' }}>{r.category_name_live || r.category_name}</td>
                  <td style={{ textAlign: 'left' }}>{r.payee || '—'}</td>
                  <td style={{ textAlign: 'left', fontSize: 11.5, color: 'var(--text-secondary)' }}>{r.description || '—'}</td>
                  <td style={{ textAlign: 'left' }}>{r.mode}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--danger)' }}>
                    ₹ {fmtNum(r.amount)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {can('expenses', 'update') && (
                        <button className="btn btn-sm" onClick={() => openEdit(r)}>✏️</button>
                      )}
                      {can('expenses', 'delete') && (
                        <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirm(r)}>🗑</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('expenses.deleteExpenseTitle')}
        message={confirm ? t('expenses.deleteExpenseMessage', { voucher: confirm.voucher_no }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={t('common.delete')}
      />
    </div>
  );
}
